import { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { getMyVotes } from "../../../store/voting/actions";
import VotingIDCForm from "../components/VotingIDCForm";
import moment from "moment";
import VoteDetailsDialog from "../components/VoteDetailsDialog";

const IDCVotes = (props) => {
  const { loading, user, myVotes, getMyVotes } = props;
  const [loadingVotes, setLoadingVotes] = useState(true);
  const [wasVoted, setWasVoted] = useState(false);
  const [showVoteCertificate, setVoteCertificate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showVotingForm, setShowVotingForm] = useState(false);
  const [selectedVote, setSelectedVote] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const filteredData = myVotes.filter((item) =>
    (item.names || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    if (
      ["2000000000-010200000000000000"].includes(
        user?.selectedEntitySector?.entityId
      )
    )
      getMyVotes(setLoadingVotes, 2);

    return () => {
      setLoadingVotes(true);
    };
  }, []);

  useEffect(() => {
    if (myVotes.length) {
      setWasVoted(
        !!myVotes.find((item) => item.fiscalYearId === user?.RBM_fiscalYear?.id)
      );
    } else setWasVoted(false);
  }, [myVotes]);

  // Open dialog
  const handleViewDetails = (vote) => {
    setSelectedVote(vote);
    setIsDetailsOpen(true);
  };

  // Close dialog
  const handleCloseDetails = () => {
    setSelectedVote(null);
    setIsDetailsOpen(false);
  };

  const skeletonArray = Array(8).fill(0);

  return (
    <>
      <div className="row w-100 my-3 px-4  ">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span className="text-uppercase text-primary font-weight-bold">
              My Votes
            </span>

            <div className="d-flex align-items-center">
              <TextField
                label="Search by Name"
                variant="outlined"
                size="small"
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: 250 }}
                className="mr-2"
              />
              <Button
                variant="contained"
                size="small"
                startIcon={
                  <span className="material-icons-round">post_add</span>
                }
                onClick={() => setShowVotingForm(true)}
                disabled={
                  loading ||
                  loadingVotes ||
                  !!wasVoted ||
                  !["2000000000-010200000000000000"].includes(
                    user?.selectedEntitySector?.entityId
                  )
                }
              >
                Vote
              </Button>
            </div>
          </div>

          {/* voted employees */}
          <div className="row mt-3">
            {loadingVotes ? (
              skeletonArray.map((_, index) => (
                <div className="col-12 mb-4" key={index}>
                  {/* Skeleton Card */}
                  <Card className="vote-card rounded h-100 border">
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Skeleton variant="circular" width={40} height={40} />
                        <Box ml={2} flex="1">
                          <Skeleton width="80%" height={24} />
                          <Skeleton width="60%" height={20} />
                        </Box>
                      </Box>

                      <Skeleton width="60%" height={20} />
                      <Skeleton width="70%" height={20} />
                      <Skeleton width="50%" height={20} />
                      <Skeleton width="60%" height={20} />

                      <Box mt={2} mb={2}>
                        <Skeleton
                          variant="rectangular"
                          width="100px"
                          height={24}
                        />
                      </Box>

                      <Skeleton width="80%" height={20} />

                      <Box mt={3}>
                        <Skeleton
                          variant="rectangular"
                          width="100%"
                          height={36}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </div>
              ))
            ) : filteredData.length > 0 ? (
              filteredData.map((vote) => (
                <div className="col-lg-3 col-md-4 mb-4" key={vote.id}>
                  <Card
                    className="vote-card h-100"
                    sx={{
                      border: `2px solid #e0e0e0`,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      borderRadius: 3,
                      p: 2,
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Avatar>{vote.names.charAt(0)}</Avatar>
                        <Box ml={2}>
                          <Typography variant="h6" fontWeight="bold">
                            {vote.names}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            fontSize="0.9rem"
                          >
                            {vote.positionName}
                          </Typography>
                        </Box>
                      </Box>

                      <Typography variant="body2">
                        <strong>Fiscal Year:</strong> {vote.fiscalYearName}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Group:</strong> {vote.groupName}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Total Votes:</strong> {vote.votes}
                      </Typography>

                      <div className="badge-container mt-2 mb-2">
                        <span className={`badge badge-info`}>
                          {vote.voteType}
                        </span>
                      </div>

                      <Typography
                        variant="body2"
                        color="textSecondary"
                        className="mb-3"
                      >
                        Voted On: {moment(vote.votedOn).format("ll")}
                      </Typography>
                    </CardContent>
                  </Card>
                </div>
              ))
            ) : (
              // No Votes Message
              <div className="col text-center mt-5">
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  😕 No votes have been made yet.
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Start voting to see your voting history here!
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>

      {showVotingForm && (
        <VotingIDCForm
          showVotingForm={showVotingForm}
          setShowVotingForm={setShowVotingForm}
        />
      )}

      {isDetailsOpen && (
        <VoteDetailsDialog
          open={isDetailsOpen}
          onClose={handleCloseDetails}
          voteData={selectedVote}
        />
      )}

      {/* {cancelPlan && (
        <AlertDialog
          status={true}
          handleClose={() => setCancelPlan(null)}
          handleConfirm={() => handleCancelLeavePlan(cancelPlan.id)}
          title="Cancel Leave Plan?"
          desc="Are you sure you want to cancel this leave plan?"
          disabled={loading || loadingVotes || isSubmitting}
        />
      )} */}

      {/* {!!showVoteCertificate && !!showVoteCertificate?.letterReferenceId && (
        <PreviewPdfUrl
          showDocument={true}
          setShowDocument={() => setshowVoteCertificate(null)}
          document={{
            name: `View Authorization Letter`,
            url:
              process.env.REACT_APP_FILES_URL +
              "/preview/" +
              showVoteCertificate?.letterReferenceId,
          }}
        />
      )} */}
    </>
  );
};

const mapStateToProps = ({ env, loading, user, fiscalYears, myVotes }) => {
  return { env, loading, user, fiscalYears, myVotes };
};

export default connect(mapStateToProps, {
  getMyVotes,
})(IDCVotes);
